import dayjs from 'dayjs';
import { AccountBalanceChart } from '@monorepo/types';
import { CHART_CONFIG, FILTER_DATA_LIMITS } from '../constants';
import { ChartData, FilterType } from '../types';

/**
 * Filters balance chart data based on the selected time period
 */
export const filterBalanceData = (
  data: AccountBalanceChart[],
  filter: FilterType,
): AccountBalanceChart[] => {
  const limit = FILTER_DATA_LIMITS[filter];

  if (limit === null) {
    return data;
  }

  return data.slice(0, limit);
};

/**
 * Deduplicates balance chart data by formatted timestamp (HH:mm:ss DD/MM), keeping the last value for each formatted timestamp.
 * This fixes the tooltip misalignment bug when multiple positions close at the same time.
 * Uses formatted timestamps to match the label format, ensuring entries within the same second are collapsed.
 *
 * @param data - Array of balance chart data points
 * @returns Deduplicated array with unique formatted timestamps
 */
const deduplicateByTimestamp = (
  data: AccountBalanceChart[],
): AccountBalanceChart[] => {
  // Use a Set to track seen formatted timestamps (matching label format)
  const seen = new Set<string>();
  const result: AccountBalanceChart[] = [];

  // Iterate in reverse to find last occurrence of each formatted timestamp
  // This ensures we keep the final balance after all positions closed at the same time
  for (let i = data.length - 1; i >= 0; i--) {
    const item = data[i];
    // Format to match label format (HH:mm:ss DD/MM) to deduplicate entries within the same second
    const formattedKey = dayjs(item.date).format('HH:mm:ss DD/MM');
    if (!seen.has(formattedKey)) {
      seen.add(formattedKey);
      result.unshift(item); // Add to beginning to maintain chronological order
    }
  }

  return result;
};

/**
 * Transforms API data to chart format with base balance
 * Handles duplicate timestamps by deduplicating and keeping the last value
 */
export const transformChartData = (
  balanceChartData: AccountBalanceChart[],
  baseBalance: number,
  accountCreatedAt: string,
  selectedFilter: FilterType,
): ChartData => {
  if (!balanceChartData || balanceChartData.length === 0) {
    return { chartData: [], xLabels: [], yAxisDomain: undefined };
  }

  const filteredData = filterBalanceData(balanceChartData, selectedFilter);

  // Deduplicate timestamps to fix tooltip misalignment bug
  // Keep the last value for each timestamp (final balance after all positions close)
  const deduplicatedData = deduplicateByTimestamp(filteredData);

  // Add base balance as first point, then filtered API data
  const data = [
    baseBalance,
    ...deduplicatedData.map((item) => Number(item.value.toFixed(2))),
  ];

  const labels = [
    dayjs(accountCreatedAt).format('HH:mm:ss DD/MM'),
    ...deduplicatedData.map((item) =>
      dayjs(item.date).format('HH:mm:ss DD/MM'),
    ),
  ];

  // Calculate Y-axis domain with padding
  const yAxisDomain = calculateYAxisDomain(data);

  return {
    chartData: data,
    xLabels: labels,
    yAxisDomain,
  };
};

/**
 * Calculates Y-axis domain with padding
 */
export const calculateYAxisDomain = (data: number[]): [number, number] => {
  const minValue = Math.min(...data);
  const maxValue = Math.max(...data);
  const padding = (maxValue - minValue) * CHART_CONFIG.paddingPercentage || 10;
  const yMin = Math.max(0, minValue - padding);
  const yMax = maxValue + padding;

  return [yMin, yMax];
};

/**
 * Formats currency values for display
 */
export const formatCurrencyValue = (value: number): string => {
  return `$${Intl.NumberFormat('en-US').format(Number(value))}`;
};

/**
 * Formats currency values for y-axis labels (more compact)
 */
export const formatYAxisCurrencyValue = (value: number): string => {
  const absValue = Math.abs(value);

  if (absValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};
